import "@/assets/main.css";

import { renewUserSession, userManager } from "@/auth/oidc";
import { installClientLogger, logClientError } from "@/utils/clientLogger";
import { createPinia } from "pinia";
import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { useAuthStore } from "@/stores/auth";

import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "@mdi/font/css/materialdesignicons.css";
import * as labsComponents from "vuetify/labs/components";
import "leaflet/dist/leaflet.css";

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

installClientLogger();

const myCustomLightTheme = {
  dark: false,
  colors: {
    background: "#FFFFFF",
    surface: "#FFFFFF",
    primary: "#2d5a3d",
    "primary-darken-1": "#1f3f2b",
    secondary: "#48A9A6",
    "secondary-darken-1": "#018786",
    error: "#B00020",
    info: "#2196F3",
    success: "#4CAF50",
    warning: "#FB8C00",
  },
  variables: {
    "border-color": "#000000",
    "border-opacity": 0.12,
    "border-radius": 15,
  },
};

const vuetify = createVuetify({
  theme: {
    defaultTheme: "myCustomLightTheme",
    themes: { myCustomLightTheme },
  },
  icons: { defaultSet: "mdi" },
  defaults: {
    VTextField: {
      variant: "outlined",
      density: "comfortable",
      class: "mb-4",
      color: "primary",
      hideDetails: "auto",
    },
    VTextarea: {
      variant: "outlined",
      density: "comfortable",
      class: "mb-4",
      color: "primary",
      hideDetails: "auto",
    },
    VSelect: {
      variant: "outlined",
      density: "comfortable",
      class: "mb-4",
      color: "primary",
      hideDetails: "auto",
    },
  },
  components: {
    ...components,
    ...labsComponents,
  },
  directives,
});

const app = createApp(App);
const pinia = createPinia();

app.use(Toast, {
  position: "top-right",
  timeout: 2500,
  closeOnClick: true,
  pauseOnHover: true,
  hideProgressBar: true,
  maxToasts: 5,
  newestOnTop: true,
  toastDefaults: { containerClassName: "ww-toasts" },
});

app.use(pinia);
app.use(router);
app.use(vuetify);

(async () => {
  try {
    // settings init hook — reserved
  } catch (e) {
    console.error("Settings init failed", e);
  } finally {
    app.mount("#app");
  }

  const auth = useAuthStore();

  userManager.events.addAccessTokenExpired(() => {
    renewUserSession().catch((err) => console.error("Silent renew failed", err));
  });

  userManager.events.addSilentRenewError((err) => {
    console.error("Silent renew error", err);
    logClientError("error", "oidc.silentRenewError", err);
    auth.user = null;
  });

  userManager.events.addUserSignedOut(() => {
    logClientError("info", "oidc.userSignedOut", "User signed out remotely");
    auth.user = null;
    auth.login();
  });

  window.addEventListener("online", async () => {
    try {
      await renewUserSession();
    } catch (err) {
      console.error("Silent renew (online) failed", err);
    }
  });
})();
